export interface TextLink {
	// rectangle in pdf coordinates [x1, y1, x2, y2]
	rect: number[];
	url: string;
}

interface Segment {
	// character range of the text item inside the line text
	start: number;
	end: number;
	x: number;
	y: number;
	width: number;
	height: number;
}

interface Line {
	text: string;
	y: number;
	endX: number;
	segments: Segment[];
}

const EMAIL_PATTERN = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;
const URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"'«»]+/gi;
// "_" is used as separator in the tabula layout, e.g. "www.tabula.ch_ CHEFREDAKTORIN"
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"»_–-]+$/;

/**
 * Finds web and e-mail addresses in the text of a pdf page (like pdf viewers do for pdfs without link annotations).
 * Text items of pdf.js are joined to lines first, because a layout program often splits an address into several items.
 * Addresses broken over two lines are not detected.
 */
export function findTextLinks(items: any[]): TextLink[] {
	const links: TextLink[] = [];

	for (const line of getLines(items)) {
		const taken: number[][] = [];

		// e-mails first, so "info@www.example.ch" is not detected as web address as well
		for (const pattern of [EMAIL_PATTERN, URL_PATTERN]) {
			pattern.lastIndex = 0;
			let match: RegExpExecArray | null;
			while ((match = pattern.exec(line.text))) {
				const text = match[0].replace(TRAILING_PUNCTUATION, '');
				const start = match.index;
				const end = start + text.length;
				if (text.length < 5 || taken.some(([s, e]) => start < e && end > s)) {
					continue;
				}
				taken.push([start, end]);

				const rect = getRect(line.segments, start, end);
				if (rect) {
					links.push({ rect, url: toUrl(text) });
				}
			}
		}
	}

	return links;
}

function getLines(items: any[]): Line[] {
	const lines: Line[] = [];
	let current: Line | null = null;

	for (const item of items) {
		// marked content entries have no text
		if (typeof item.str !== 'string') {
			continue;
		}

		const [, b, , d, x, y] = item.transform;
		// rotated text is not supported, it ends the current line (italic text is only skewed, it has c != 0 but b = 0)
		if (Math.abs(b) > 0.01) {
			current = null;
			continue;
		}

		const height = item.height || Math.abs(d);
		if (item.str.length > 0) {
			const sameLine = current && Math.abs(y - current.y) < height * 0.5 && x >= current.endX - height;
			if (current && sameLine) {
				// a visible gap between two items is a word break, otherwise "www.a.ch" + "und" would become one address
				const gap = x - current.endX;
				if (gap > height * 0.2 && current.text.endsWith(' ') === false && item.str.startsWith(' ') === false) {
					current.text += ' ';
				}
			} else {
				current = { text: '', y, endX: x, segments: [] };
				lines.push(current);
			}

			const start = current.text.length;
			current.text += item.str;
			current.segments.push({ start, end: current.text.length, x, y, width: item.width, height });
			current.endX = x + item.width;
		}

		if (item.hasEOL) {
			current = null;
		}
	}

	return lines;
}

function getRect(segments: Segment[], start: number, end: number): number[] | null {
	let x1 = Infinity;
	let y1 = Infinity;
	let x2 = -Infinity;
	let y2 = -Infinity;

	for (const segment of segments) {
		if (segment.end <= start || segment.start >= end) {
			continue;
		}

		// the character positions inside an item are approximated proportionally
		const length = segment.end - segment.start;
		const from = Math.max(start, segment.start) - segment.start;
		const to = Math.min(end, segment.end) - segment.start;
		x1 = Math.min(x1, segment.x + (segment.width * from) / length);
		x2 = Math.max(x2, segment.x + (segment.width * to) / length);
		// y is the baseline, the box covers descenders and ascenders
		y1 = Math.min(y1, segment.y - segment.height * 0.25);
		y2 = Math.max(y2, segment.y + segment.height * 0.9);
	}

	return x1 < x2 ? [x1, y1, x2, y2] : null;
}

function toUrl(text: string): string {
	if (/^https?:\/\//i.test(text)) {
		return text;
	}
	if (/^www\./i.test(text)) {
		return `https://${text}`;
	}
	return `mailto:${text}`;
}
